import { SortDirection, SortType, useHomeStore } from '../../store/home-store';
import {
  CategoryCryptoIcon,
  CategoryDailyIcon,
  CategoryDefaultIcon,
  CategoryMacroIcon,
  CategoryMiddleEastIcon,
  CategorySportsIcon,
  CategoryTechIcon,
  InteractLiveIcon,
  InteractNewIcon,
  InteractSortIcon,
  InteractTrendingIcon,
  InteractWatchedIcon,
  InteractWatchIcon,
} from '@/client/assets';
import { ApiTranslation } from '@/client/components/api-translation';
import { AuthedButton } from '@/client/components/authed-button';
import { ScrollArea, ScrollBar } from '@/client/components/ui/scroll-area';
import { Skeleton } from '@/client/components/ui/skeleton';
import { useAppSettingStore } from '@/client/store/app-store';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { categoryLabel } from '@/client/utils/category-i18n';
import { useCategories } from '@/libs/react-sdk/api/use-categories';
import { motion } from 'framer-motion';
import { memo, ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type FilterItem = {
  value: string;
  label: ReactNode | string;
  // Required: every tab carries a glyph. The predecessor fell back to a coloured
  // dot when this was unset, which is what left the category tabs as anonymous
  // bullets. Making it non-optional means that state cannot come back.
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  translate?: boolean;
  // Label is already a fully-resolved display string (e.g. via categoryLabel) —
  // render verbatim, skip both t() lookup and the ApiTranslation fallback.
  resolved?: boolean;
} & {
  translate?: true;
  label: string;
};

// Category icon mapping. Matched on substrings of the admin category name so a
// tenant can call it "Tech & Culture" or "Technology" and still resolve; the
// list is ordered because "crypto" must win over a bare "economy" match.
// Everything falls through to a generic grid rather than a coloured dot -- a
// dot carries no meaning at 14px and reads as an unstyled bullet.
const CATEGORY_ICONS: [string, React.FC<React.SVGProps<SVGSVGElement>>][] = [
  ['crypto', CategoryCryptoIcon],
  ['daily', CategoryDailyIcon],
  ['middle east', CategoryMiddleEastIcon],
  ['macro', CategoryMacroIcon],
  ['econom', CategoryMacroIcon],
  ['financ', CategoryMacroIcon],
  ['politic', CategoryMacroIcon],
  ['sport', CategorySportsIcon],
  ['tech', CategoryTechIcon],
  ['science', CategoryTechIcon],
];

const getCategoryIcon = (name: string): React.FC<React.SVGProps<SVGSVGElement>> => {
  const n = name.toLowerCase();
  return CATEGORY_ICONS.find(([key]) => n.includes(key))?.[1] ?? CategoryDefaultIcon;
};

const defaultSortType = [
  {
    value: 'trending',
    label: 'trending',
    icon: InteractTrendingIcon,
  },
  {
    value: 'live',
    label: 'live',
    icon: InteractLiveIcon,
  },
  {
    value: 'new',
    label: 'new',
    icon: InteractNewIcon,
  },
] as const;

type CategoryItemProps = {
  item: FilterItem;
  translate?: boolean;
  isActive: boolean;
  onClick: () => void;
  layoutId: string;
};

export const CategoryItem = ({ item, translate = true, isActive, onClick, layoutId }: CategoryItemProps) => {
  const { t } = useTranslation(['home']);
  
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap shrink-0 outline-none"
    >
      {/* Sliding background indicator */}
      {isActive && (
        <motion.div
          layoutId={layoutId}
          className="absolute inset-0 bg-foreground rounded-full"
          initial={false}
          transition={{
            type: 'spring',
            stiffness: 500,
            damping: 35,
          }}
        />
      )}
      
      {/* Content */}
      <span
        className={cn(
          'relative z-10 flex items-center gap-1.5 transition-colors duration-200',
          isActive ? 'text-background' : 'text-muted-foreground hover:text-foreground'
        )}
      >
        <item.icon className="size-3.5 fill-current" />
        {item.resolved ? (
          item.label
        ) : item.translate || translate ? (
          t(item.label)
        ) : (
          <ApiTranslation>{item.label}</ApiTranslation>
        )}
      </span>
    </button>
  );
};

export const CategoryItemSkeleton = memo(() => {
  return <Skeleton className="h-8 w-20 rounded-full mx-1" />;
});

CategoryItemSkeleton.displayName = 'CategoryItemSkeleton';

export const CategorySkeleton = ({ len }: { len: number }) => {
  return Array.from({ length: len }).map((_, index) => <CategoryItemSkeleton key={`CategoryItemSkeleton-${index}`} />);
};

type SortItemProps = {
  children?: React.ReactNode;
  sortDirection?: SortDirection;
  setSortDirection?: (sortDirection: SortDirection) => void;
};

export const SortItem = ({ children, sortDirection, setSortDirection }: SortItemProps) => {
  const [direction, setDirection] = useState<SortDirection>('default');
  useEffect(() => {
    if (sortDirection) setDirection(sortDirection);
  }, [sortDirection]);
  const handleToggle = () => {
    const next = direction == 'default' ? 'desc' : direction == 'desc' ? 'asc' : 'default';
    setDirection(next);
    setSortDirection?.(next);
  };
  return (
    <div
      className="flex items-center gap-1 px-2 py-1.5 text-sm cursor-pointer whitespace-nowrap text-muted-foreground hover:text-foreground transition-colors"
      onClick={handleToggle}
    >
      {children}
      <InteractSortIcon
        className={cn(
          'size-4 fill-muted-foreground/30 transition-colors',
          direction === 'asc' && '[&_path:first-child]:fill-foreground',
          direction === 'desc' && '[&_path:last-child]:fill-foreground'
        )}
      />
    </div>
  );
};

export const TheMarketListFilterBar = () => {
  const { t } = useTranslation(['home']);
  const category = useHomeStore(state => state.category);
  const setCategory = useHomeStore(state => state.setCategory);
  const sortDirection = useHomeStore(state => state.sortDirection);
  const setSortDirection = useHomeStore(state => state.setSortDirection);
  const sortType = useHomeStore(state => state.sortType);
  const setSortType = useHomeStore(state => state.setSortType);
  const baseTokenAddress = useAppSettingStore(state => state.baseToken.address);
  const { data, isLoading } = useCategories(api, { baseTokenAddress });
  const categories = useMemo(
    () =>
      data?.categories
        .sort((a, b) => b.weight - a.weight)
        .map(cat => ({
          label: categoryLabel(t, cat.name),
          value: cat.id,
          icon: getCategoryIcon(cat.name),
          resolved: true,
        })) ?? [],
    [data?.categories, t]
  );

  // Check if current category is in the main tab group (for shared layoutId)
  const isMainCategory = useMemo(() => {
    const mainValues = [...defaultSortType.map(s => s.value), ...categories.map(c => c.value)];
    return mainValues.includes(category);
  }, [category, categories]);

  return (
    <div className="flex items-center gap-4 px-4 md:px-6 h-12 bg-background border-b border-border pos-sticky top-0 md:top-15 z-10">
      {/* Left: Category tabs */}
      <ScrollArea className="flex-1 overflow-auto">
        <div className="flex items-center gap-1">
          {defaultSortType.map((item, index) => (
            <CategoryItem
              key={`tag-${item.value}-${index}`}
              item={item}
              isActive={item.value === category}
              onClick={() => setCategory(item.value)}
              layoutId="category-tab-indicator"
            />
          ))}

          {categories.length > 0 && <div className="w-px h-5 bg-border/50 mx-1.5 shrink-0" />}

          {categories.map((item, index) => (
            <CategoryItem
              key={`tag-${item.value}-${index}`}
              item={item}
              isActive={item.value === category}
              onClick={() => setCategory(item.value)}
              layoutId="category-tab-indicator"
            />
          ))}
          {isLoading && <CategorySkeleton len={6} />}
        </div>
        <ScrollBar orientation="horizontal" className="h-1.5 cursor-pointer" />
      </ScrollArea>

      {/* Right: Sort + Watchlist */}
      <div className="flex items-center gap-1 shrink-0">
        <SortItem
          sortDirection={sortType == 'expire-time' ? sortDirection : 'default'}
          setSortDirection={direction => {
            setSortType('expire-time');
            setSortDirection(direction);
          }}
        >
          {t('expire-time')}
        </SortItem>
        <SortItem
          sortDirection={sortType == 'volume' ? sortDirection : 'default'}
          setSortDirection={direction => {
            setSortType('volume');
            setSortDirection(direction);
          }}
        >
          {t('volume')}
        </SortItem>

        <div className="w-px h-5 bg-border/50 mx-1" />

        {/* Watchlist button - separate layoutId since it's in a different group */}
        <button
          type="button"
          onClick={() => setCategory(category == 'watchlist' ? 'trending' : 'watchlist')}
          className="relative px-3 py-1.5 h-8 rounded-full text-sm font-medium outline-none"
        >
          {/* Sliding background for watchlist */}
          {category === 'watchlist' && (
            <motion.div
              layoutId="watchlist-tab-indicator"
              className="absolute inset-0 bg-foreground rounded-full"
              initial={false}
              transition={{
                type: 'spring',
                stiffness: 500,
                damping: 35,
              }}
            />
          )}
          
          <span
            className={cn(
              'relative z-10 flex items-center gap-1.5 transition-colors duration-200',
              category === 'watchlist' ? 'text-background' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {category === 'watchlist' ? (
              <InteractWatchedIcon className="size-3.5 fill-current" />
            ) : (
              <InteractWatchIcon className="size-3.5 fill-current" />
            )}
            <span className="hidden sm:inline">{t('watchlist')}</span>
          </span>
        </button>
      </div>
    </div>
  );
};
